import { SelectField } from '@atlas/ui-components'
import { Button, Input, Textarea } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { PageHeader } from '@/components/editorial/PageHeader'
import { PromptClassificationField } from '@/components/prompts/PromptClassificationField'
import type { CreatePromptInput, PromptType } from '@/domain/prompt.types'
import { lintPrompt, PROMPT_TYPE_LABEL, PROMPT_TYPE_ORDER, PromptType as Types } from '@/domain/prompt.types'
import type { Selection } from '@/domain/work-order.types'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { useClassifyPrompt, useCreatePrompt } from '@/hooks/usePrompts'

/** Suggest a kebab-case code from the name, so an author rarely types one. */
const suggestCode = (name: string): string =>
	name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 128)

export function CreatePromptPage() {
	const navigate = useNavigate()
	const { axes } = useAxesWithValues()
	const { submit, isPending, isError, error } = useCreateWithClassification()

	const [name, setName] = useState('')
	const [code, setCode] = useState('')
	const [codeEdited, setCodeEdited] = useState(false)
	const [type, setType] = useState<PromptType>(Types.USER_USE_CASE)
	const [body, setBody] = useState('')
	const [tuple, setTuple] = useState<Selection>({})

	// A new prompt has no variables yet, so any placeholder in the body is
	// undeclared. Surfaced here rather than after a failed save.
	const lint = lintPrompt(body, [])
	const effectiveCode = codeEdited ? code : suggestCode(name)
	const canSubmit = name.trim() !== '' && effectiveCode !== '' && body.trim() !== '' && lint.undeclared.length === 0

	return (
		<div>
			<PageHeader
				eyebrow="Library"
				title="New prompt"
				subtitle="Starts as a draft. Publishing later freezes it as version 1."
			/>

			<form
				className="max-w-2xl space-y-4"
				onSubmit={(e) => {
					e.preventDefault()
					if (!canSubmit) return
					submit({ code: effectiveCode, name, type, body }, tuple)
				}}
			>
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Name</span>
					<Input value={name} aria-label="Prompt name" onChange={(e) => setName(e.target.value)} />
				</label>

				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
						Code — the stable handle agents resolve by. Immutable once created.
					</span>
					<Input
						value={effectiveCode}
						aria-label="Prompt code"
						className="font-mono"
						onChange={(e) => {
							setCodeEdited(true)
							setCode(e.target.value)
						}}
					/>
				</label>

				<SelectField
					label="Type"
					ariaLabel="Prompt type"
					value={type}
					// No empty option: the type is an enum that always holds a value, so
					// offering "unset" would only add a choice the form must then reject.
					isClearable={false}
					options={PROMPT_TYPE_ORDER.map((t) => ({ value: t, label: PROMPT_TYPE_LABEL[t] }))}
					onChange={(value) => setType(value as PromptType)}
				/>

				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
						Body — add {'{{variable}}'} placeholders after creating, from the editor
					</span>
					<Textarea
						rows={10}
						value={body}
						aria-label="Prompt body"
						className="font-mono"
						onChange={(e) => setBody(e.target.value)}
					/>
				</label>

				<PromptClassificationField value={tuple} axes={axes} onChange={setTuple} />

				{lint.undeclared.length > 0 ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{lint.undeclared.map((k) => `{{${k}}}`).join(', ')} needs a variable. Create the prompt with plain text
						first, then add variables in the editor.
					</p>
				) : null}

				{isError ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{extractApiErrorMessage(error, 'The prompt could not be created. The code may already be in use.')}
					</p>
				) : null}

				<div className="flex gap-2">
					<Button type="submit" isDisabled={!canSubmit || isPending}>
						{isPending ? 'Creating…' : 'Create prompt'}
					</Button>
					<Button variant="secondary" onPress={() => void navigate('/prompts')}>
						Cancel
					</Button>
				</div>
			</form>
		</div>
	)
}

/**
 * Create a prompt and, when a tuple was picked, classify it in the same flow — so a
 * new prompt is anchored and resolvable without a second visit to its detail page.
 *
 * The classify failure is deliberately swallowed: the prompt itself was created, and
 * discarding that to report a tuple error would be worse than landing the author on
 * the prompt where the classification tab can retry.
 */
function useCreateWithClassification() {
	const navigate = useNavigate()
	const create = useCreatePrompt()
	const classify = useClassifyPrompt()

	const submit = (input: CreatePromptInput, tuple: Selection) => {
		create.mutate(input, {
			onSuccess: (prompt) => {
				const go = () => void navigate(`/prompts/${prompt.id}`)
				if (Object.keys(tuple).length === 0) return go()

				// `void` on the chain, not on an async callback: react-query expects a void
				// return here, and an async one would be an unhandled-promise handoff.
				void classify.mutateAsync({ id: prompt.id, classification: tuple }).then(go, go)
			},
		})
	}

	return {
		submit,
		isPending: create.isPending || classify.isPending,
		isError: create.isError,
		error: create.error,
	}
}
